import React, { useState, useEffect } from 'react';
import { Crown, CreditCard, AlertTriangle, CheckCircle, Clock, Upload, X } from 'lucide-react';

// Every plan includes ALL features — tiers differ only by resource limits.
// Must mirror planLimits() in api/tenant.php (the server is the enforcer).
const PLANS = [
    { id: 'trial',      label: 'Free Trial', price: 0,    color: '#5c5f62', branches: 1, staff: 2,  products: 100,  photos: 2, orders: 100 },
    { id: 'starter',    label: 'Starter',    price: 4000, color: '#008060', branches: 1, staff: 3,  products: 750,  photos: 2, orders: 750 },
    { id: 'growth',     label: 'Growth',     price: 6000, color: '#1773B0', branches: 3, staff: 8,  products: 1500, photos: 3, orders: 1500 },
    { id: 'enterprise', label: 'Pro',        price: 8000, color: '#50248f', branches: 6, staff: 15, products: 2500, photos: 5, orders: 2500 },
];

const BillingPanel = ({ apiCall, showToast }) => {
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPayForm, setShowPayForm] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('starter');
    const [payMethod, setPayMethod] = useState('JazzCash');
    const [payRef, setPayRef] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        apiCall('getTenantBilling').then(res => {
            if (res && !res.error) setBilling(res);
        });
    }, []);

    const currentPlan = PLANS.find(p => p.id === (billing?.plan || 'trial')) || PLANS[0];
    const trialDaysLeft = billing?.trial_ends_at
        ? Math.max(0, Math.ceil((new Date(billing.trial_ends_at) - Date.now()) / 86400000))
        : null;

    const handleSubmitProof = async (e) => {
        e.preventDefault();
        if (!payRef.trim()) return showToast('Enter a payment reference / transaction ID', 'error');
        setSubmitting(true);
        const res = await apiCall('submitPaymentProof', {
            plan: selectedPlan,
            method: payMethod,
            ref: payRef,
            amount: PLANS.find(p => p.id === selectedPlan)?.price || 0,
        });
        setSubmitting(false);
        if (res?.message) {
            showToast(res.message);
            setShowPayForm(false);
            setPayRef('');
        } else {
            showToast(res?.error || 'Failed to submit', 'error');
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-[#e1e3e5] overflow-hidden">
            {/* Header */}
            <div className="p-5 border-b border-[#e1e3e5] bg-gradient-to-r from-[#1a1a1a] to-[#303030] flex items-center gap-3">
                <Crown size={20} className="text-[#fbbf24]"/>
                <h3 className="font-bold text-white">Subscription & Billing</h3>
                {billing && (
                    <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                        billing.status === 'active' ? 'bg-[#008060] text-white' :
                        billing.status === 'trial'  ? 'bg-[#b98900] text-white' :
                        'bg-[#d82c0d] text-white'}`}>
                        {billing.status?.toUpperCase() || 'ACTIVE'}
                    </span>
                )}
            </div>

            <div className="p-5 space-y-5">
                {/* Current plan status */}
                {billing ? (
                    <div className="flex items-center justify-between p-4 bg-[#f6f7f8] rounded-xl border border-[#e1e3e5]">
                        <div>
                            <p className="text-xs font-bold text-[#5c5f62] uppercase">Current Plan</p>
                            <p className="text-lg font-black mt-0.5" style={{ color: currentPlan.color }}>{currentPlan.label}</p>
                            {trialDaysLeft !== null && (
                                <p className={`text-xs flex items-center gap-1 mt-1 ${trialDaysLeft <= 3 ? 'text-[#d82c0d] font-bold' : 'text-[#5c5f62]'}`}>
                                    <Clock size={11}/> {trialDaysLeft} days left in trial
                                </p>
                            )}
                        </div>
                        <div className="text-right space-y-0.5">
                            <p className="text-xs text-[#5c5f62]">{billing.branch_count || 0} / {currentPlan.branches} branches</p>
                            <p className="text-xs text-[#5c5f62]">{billing.staff_count || 0} / {currentPlan.staff} staff</p>
                            <p className="text-xs text-[#5c5f62]">{(billing.product_count || 0).toLocaleString()} / {currentPlan.products.toLocaleString()} products</p>
                            <p className={`text-xs font-semibold ${(billing.order_count_month || 0) >= currentPlan.orders ? 'text-[#d82c0d]' : 'text-[#5c5f62]'}`}>{(billing.order_count_month || 0).toLocaleString()} / {currentPlan.orders.toLocaleString()} orders this month</p>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 bg-[#f6f7f8] rounded-xl animate-pulse h-20"/>
                )}

                <p className="text-xs text-[#5c5f62] text-center -mb-1">Every plan includes all features — POS, Khata, Qist, couriers, WhatsApp, all reports. Plans differ only by capacity.</p>

                {/* Plan cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {PLANS.filter(p => p.id !== 'trial').map(plan => (
                        <div key={plan.id} onClick={() => { setSelectedPlan(plan.id); setShowPayForm(true); }}
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                billing?.plan === plan.id
                                    ? 'border-[#008060] bg-[#e4fcf4]'
                                    : 'border-[#e1e3e5] hover:border-[#c9cccf] bg-white hover:shadow-sm'}`}>
                            <p className="font-black text-sm mb-1" style={{ color: plan.color }}>{plan.label}</p>
                            <p className="text-base font-black text-[#1a1a1a]">₨{plan.price.toLocaleString()}<span className="text-[10px] font-normal text-[#5c5f62]">/mo</span></p>
                            <div className="mt-2 space-y-0.5 text-[10px] text-[#5c5f62]">
                                <p>{plan.branches} branch{plan.branches > 1 ? 'es' : ''}</p>
                                <p>{plan.staff} staff logins</p>
                                <p>{plan.products.toLocaleString()} products</p>
                                <p>{plan.orders.toLocaleString()} orders/month</p>
                                <p>{plan.photos} photos/product</p>
                            </div>
                            {billing?.plan === plan.id ? (
                                <p className="text-[10px] font-bold text-[#008060] mt-2 flex items-center gap-1"><CheckCircle size={10}/> Active</p>
                            ) : (
                                <p className="text-[10px] font-bold text-[#1773B0] mt-2">Upgrade →</p>
                            )}
                        </div>
                    ))}
                </div>

                {/* Payment note */}
                <div className="bg-[#fff5ea] border border-[#b98900]/30 rounded-lg p-3 text-xs text-[#8a6116]">
                    <p className="font-bold mb-1 flex items-center gap-1"><CreditCard size={11}/> How to pay</p>
                    <p>Send payment via JazzCash/Easypaisa/Bank Transfer to <strong>03xx-xxxxxxx</strong>, then click a plan above and enter your transaction reference. We activate within 24 hours.</p>
                </div>
            </div>

            {/* Payment proof modal */}
            {showPayForm && (
                <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-sm rounded-xl shadow-lg border border-[#e1e3e5] overflow-hidden">
                        <div className="p-4 border-b border-[#e1e3e5] bg-[#f6f7f8] flex justify-between items-center">
                            <h3 className="font-bold text-[#1a1a1a] flex items-center gap-2"><Upload size={16} className="text-[#1773B0]"/> Submit Payment Proof</h3>
                            <button onClick={() => setShowPayForm(false)}><X size={18} className="text-[#5c5f62]"/></button>
                        </div>
                        <form onSubmit={handleSubmitProof} className="p-5 space-y-4">
                            <div>
                                <label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Plan</label>
                                <select value={selectedPlan} onChange={e=>setSelectedPlan(e.target.value)} className="w-full p-2.5 border border-[#c9cccf] rounded-lg text-sm bg-white font-semibold outline-none">
                                    {PLANS.filter(p=>p.id!=='trial').map(p=><option key={p.id} value={p.id}>{p.label} — ₨{p.price.toLocaleString()}/mo</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Payment Method</label>
                                <select value={payMethod} onChange={e=>setPayMethod(e.target.value)} className="w-full p-2.5 border border-[#c9cccf] rounded-lg text-sm bg-white outline-none">
                                    {['JazzCash','Easypaisa','Raast','Bank Transfer','HBL Konnect'].map(m=><option key={m}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#5c5f62] uppercase block mb-1">Transaction ID / Reference</label>
                                <input required value={payRef} onChange={e=>setPayRef(e.target.value)} placeholder="e.g. TXN123456789" className="w-full p-2.5 border border-[#c9cccf] rounded-lg text-sm outline-none font-mono"/>
                            </div>
                            <div className="bg-[#e4fcf4] border border-[#008060]/20 rounded-lg p-3 text-xs text-[#006e52]">
                                <p className="font-bold">Amount: ₨{(PLANS.find(p=>p.id===selectedPlan)?.price||0).toLocaleString()}/mo</p>
                                <p>Our team will verify and activate your plan within 24 hours.</p>
                            </div>
                            <button type="submit" disabled={submitting} className="w-full bg-[#1773B0] text-white py-3 rounded-lg font-semibold hover:bg-[#1260a0] transition-colors disabled:opacity-50">
                                {submitting ? 'Submitting…' : 'Submit Payment Proof'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingPanel;
